import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency } from '../utils/format';
import { MONTH_NAMES, monthLabel } from '../utils/month';
import { downloadFile } from '../utils/downloadFile';
import { fibeTokens } from '../../../theme';
import { FibeDialog, DialogAlert, fieldLabelSx } from '../components/FibeDialog';

const PAGE_SIZE = 15;
const ALL_PERIODS_FETCH_LIMIT = 200;

const STATUS_BADGES = {
    generated: { badgeClass: 'badge-gray', label: 'Generated' },
    approved: { badgeClass: 'badge-blue', label: 'Approved' },
    zoho_created: { badgeClass: 'badge-zoho', label: 'Zoho Created' },
    partially_settled: { badgeClass: 'badge-yellow', label: 'Partially Settled' },
    fully_settled: { badgeClass: 'badge-green', label: 'Fully Settled' },
};
function statusBadgeFor(status) { return STATUS_BADGES[status] ?? { badgeClass: 'badge-gray', label: status }; }

const BADGE_CHIP_PROPS = {
    'badge-gray': { variant: 'fibePill', color: 'default' },
    'badge-blue': { variant: 'fibePill', color: 'info' },
    'badge-zoho': { variant: 'fibePillZoho' },
    'badge-yellow': { variant: 'fibePill', color: 'warning' },
    'badge-green': { variant: 'fibePill', color: 'success' },
};

function StatusChip({ status }) {
    const badge = statusBadgeFor(status);
    const chipProps = BADGE_CHIP_PROPS[badge.badgeClass] ?? { variant: 'fibePill', color: 'default' };
    return <Chip {...chipProps} label={badge.label} />;
}

const FILTER_MENU_PROPS = {
    PaperProps: {
        sx: {
            '& .MuiMenuItem-root': {
                fontFamily: fibeTokens.font.family,
                fontSize: 12.5,
                fontWeight: 400,
                minHeight: 'auto',
                padding: '8px 12px',
            },
        },
    },
};

function RefreshGlyph() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function DocumentGlyph() {
    return (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2Z" />
            <path d="M9 9h6M9 13h4" />
        </svg>
    );
}

function ErrorBanner({ title, message, onRetry, retryLabel = 'Retry' }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    {title}
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            {onRetry && (
                <Box sx={{ flex: 'none' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onRetry}>{retryLabel}</Button>
                </Box>
            )}
        </Box>
    );
}

function InvoicesEmptyState({ onGenerate }) {
    return (
        <Box sx={{ textAlign: 'center', padding: '40px 18px' }}>
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <DocumentGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No invoices generated yet.
            </Box>
            <Box
                sx={{
                    fontFamily: fibeTokens.font.family,
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: fibeTokens.color.textMuted,
                    margin: '5px 0 13px',
                }}
            >
                Generate a monthly invoice to bill Fibe for the billable milestones in a period.
            </Box>
            <Button variant="fibePrimary" size="small" onClick={onGenerate}>+ Generate New Invoice</Button>
        </Box>
    );
}

const INVOICE_SKELETON_COLUMNS = [
    { width: 104 },
    { width: 78 },
    { width: 64 },
    { width: 26 },
    { width: 90, pill: true },
    { width: 64 },
    { width: 56, button: true },
];

function InvoicesTableSkeleton() {
    return (
        <>
            <Box
                sx={{
                    height: `${fibeTokens.density.tableHeaderHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    background: fibeTokens.color.rowHover,
                    borderBottom: `1px solid ${fibeTokens.color.border}`,
                }}
            >
                <Skeleton variant="text" width={160} height={10} />
            </Box>
            {Array.from({ length: 6 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 14px',
                        borderBottom: rowIndex < 5 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {INVOICE_SKELETON_COLUMNS.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill || col.button ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : col.button ? 22 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : col.button ? '7px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </>
    );
}

function DetailPanelSkeleton() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: '0 6px 20px -10px rgba(16,24,40,.16)',
                overflow: 'hidden',
                marginBottom: '14px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: fibeTokens.density.cardHeaderPadding,
                    background: fibeTokens.color.rowHover,
                    borderBottom: `1px solid ${fibeTokens.color.border}`,
                }}
            >
                <Skeleton variant="text" width={220} height={16} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={58} sx={{ borderRadius: '10px' }} />
                ))}
            </Box>
            <Box sx={{ padding: '0 18px 18px' }}>
                <Skeleton variant="rounded" height={130} sx={{ borderRadius: '8px' }} />
            </Box>
        </Box>
    );
}

function GenerateInvoiceModal({ onClose, onGenerated }) {
    const now = new Date();
    const [billingMonth, setBillingMonth] = useState(now.getMonth() + 1);
    const [billingYear, setBillingYear] = useState(now.getFullYear());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleGenerate = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        axiosInstance
            .post('/fibe-billing/invoices/generate', { billingMonth, billingYear })
            .then(() => {
                onGenerated();
                onClose();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not generate invoice.'))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <FibeDialog
            open
            onClose={onClose}
            tone="red"
            icon={<DocumentGlyph />}
            title="Generate Monthly Invoice"
            subtitle="Aggregates all billable milestones for the period into one invoice"
            footer={(
                <>
                    <Button variant="fibeSecondary" onClick={onClose}>Cancel</Button>
                    <Button variant="fibePrimary" onClick={handleGenerate} disabled={isSubmitting}>
                        {isSubmitting ? 'Generating…' : 'Generate Invoice'}
                    </Button>
                </>
            )}
        >
            <Box sx={{ display: 'flex', gap: '12px' }}>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Billing Month</Box>
                    <Select size="fibeMd" fullWidth value={billingMonth} onChange={(e) => setBillingMonth(Number(e.target.value))} MenuProps={FILTER_MENU_PROPS}>
                        {MONTH_NAMES.map((name, index) => (
                            <MenuItem key={name} value={index + 1}>{name}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box component="label" sx={fieldLabelSx()}>Billing Year</Box>
                    <OutlinedInput size="fibeMd" fullWidth type="number" value={billingYear} onChange={(e) => setBillingYear(Number(e.target.value))} />
                </Box>
            </Box>

            <DialogAlert tone="info">
                This aggregates all <strong>billable</strong> milestones for this period into a single invoice and
                transitions them to <strong>invoiced</strong> status. This action cannot be undone.
            </DialogAlert>

            {errorMessage && <ErrorBanner title="Could not generate invoice" message={errorMessage} />}
        </FibeDialog>
    );
}

function RecordSettlementForm({ invoice, onRecorded }) {
    const remaining = Number(invoice.totalAmount) - Number(invoice.totalSettled);
    const [paymentAmount, setPaymentAmount] = useState(remaining);
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
    const [utrReference, setUtrReference] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleSubmit = () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        axiosInstance
            .post(`/fibe-billing/invoices/${invoice.id}/settlements`, {
                paymentAmount: Number(paymentAmount),
                paymentDate,
                utrReference,
            })
            .then(() => {
                setUtrReference('');
                onRecorded();
            })
            .catch((error) => setErrorMessage(error.response?.data?.message ?? 'Could not record settlement.'))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: fibeTokens.density.cardPadding,
                marginBottom: '14px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
                <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                    Record Payment from Fibe
                </Box>
                <Chip variant="fibePill" color="warning" label={`${formatCurrency(remaining)} remaining`} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                <Box>
                    <Box
                        component="label"
                        sx={{ display: 'block', fontFamily: fibeTokens.font.family, ...fibeTokens.font.fieldLabel, marginBottom: '6px' }}
                    >
                        Payment Amount (Remaining: {formatCurrency(remaining)})
                    </Box>
                    <OutlinedInput
                        size="fibeMd"
                        type="number"
                        fullWidth
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                </Box>
                <Box>
                    <Box
                        component="label"
                        sx={{ display: 'block', fontFamily: fibeTokens.font.family, ...fibeTokens.font.fieldLabel, marginBottom: '6px' }}
                    >
                        Payment Date
                    </Box>
                    <OutlinedInput
                        size="fibeMd"
                        type="date"
                        fullWidth
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                    />
                </Box>
                <Box>
                    <Box
                        component="label"
                        sx={{ display: 'block', fontFamily: fibeTokens.font.family, ...fibeTokens.font.fieldLabel, marginBottom: '6px' }}
                    >
                        UTR / Reference
                    </Box>
                    <OutlinedInput
                        size="fibeMd"
                        fullWidth
                        placeholder="e.g. NEFT2026061523456"
                        value={utrReference}
                        onChange={(e) => setUtrReference(e.target.value)}
                    />
                </Box>
            </Box>

            {errorMessage && (
                <Box sx={{ marginTop: '14px' }}>
                    <ErrorBanner title="Could not record settlement" message={errorMessage} />
                </Box>
            )}

            <Box sx={{ marginTop: '16px' }}>
                <Button variant="fibeSuccess" onClick={handleSubmit} disabled={isSubmitting || !utrReference}>
                    {isSubmitting ? 'Recording...' : 'Record Settlement'}
                </Button>
            </Box>
        </Box>
    );
}

function InvoiceDetailPanel({ invoiceId, onClose, onChanged }) {
    const [invoice, setInvoice] = useState(null);
    const [settlements, setSettlements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [actionError, setActionError] = useState(null);

    const loadDetail = useCallback(() => {
        setIsLoading(true);
        setLoadError(null);
        Promise.all([
            axiosInstance.get(`/fibe-billing/invoices/${invoiceId}`),
            axiosInstance.get(`/fibe-billing/invoices/${invoiceId}/settlements`),
        ])
            .then(([invoiceRes, settlementsRes]) => {
                setInvoice(invoiceRes.data.data);
                setSettlements(settlementsRes.data.data);
            })
            .catch((error) => setLoadError(error.response?.data?.message ?? 'Could not load this invoice.'))
            .finally(() => setIsLoading(false));
    }, [invoiceId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const refreshAfterAction = () => {
        loadDetail();
        onChanged();
    };

    const handleApprove = () => {
        setActionError(null);
        axiosInstance
            .post(`/fibe-billing/invoices/${invoiceId}/approve`)
            .then(refreshAfterAction)
            .catch((error) => setActionError(error.response?.data?.message ?? 'Could not approve invoice.'));
    };

    const handleExport = () => {
        setActionError(null);
        downloadFile(`/fibe-billing/invoices/${invoiceId}/export`, `FIB-invoice-${invoice.invoiceNumber}.xlsx`)
            .catch((error) => setActionError(error.response?.data?.message ?? 'Could not export invoice.'));
    };

    if (isLoading) {
        return <DetailPanelSkeleton />;
    }

    if (loadError || !invoice) {
        return (
            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    padding: fibeTokens.density.cardPadding,
                    marginBottom: '14px',
                }}
            >
                <ErrorBanner title="Could not load this invoice" message={loadError ?? 'Could not load this invoice.'} onRetry={loadDetail} />
                <Box sx={{ marginTop: '12px' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onClose}>Close</Button>
                </Box>
            </Box>
        );
    }

    const detailStats = [
        { label: 'Period', value: monthLabel(invoice.billingMonth, invoice.billingYear) },
        { label: 'Line Items', value: invoice.lineItemCount },
        { label: 'Invoice Total', value: formatCurrency(invoice.totalAmount), tone: fibeTokens.color.success },
        { label: 'Settled', value: formatCurrency(invoice.totalSettled) },
    ];

    const showActionsRow = invoice.invoiceStatus === 'generated';

    return (
        <>
            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: '0 6px 20px -10px rgba(16,24,40,.16)',
                    overflow: 'hidden',
                    marginBottom: '14px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: fibeTokens.density.cardHeaderPadding,
                        background: fibeTokens.color.rowHover,
                        borderBottom: `1px solid ${fibeTokens.color.border}`,
                    }}
                >
                    <Box sx={{ width: '3px', height: '20px', borderRadius: '9px', background: fibeTokens.color.primary, flex: 'none' }} />
                    <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                        Invoice — {invoice.invoiceNumber}
                    </Box>
                    <StatusChip status={invoice.invoiceStatus} />
                    <Box sx={{ flex: 1 }} />
                    <Button variant="fibeSecondary" size="small" onClick={handleExport}>Export Excel</Button>
                    <Button variant="fibeGhost" size="small" onClick={onClose}>Close</Button>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${detailStats.length}, 1fr)`, gap: '12px', padding: '16px 18px 14px' }}>
                    {detailStats.map((stat) => (
                        <Box
                            key={stat.label}
                            sx={{ border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px', padding: '11px 13px', background: fibeTokens.color.rowHover }}
                        >
                            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 10, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {stat.label}
                            </Box>
                            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 18, color: stat.tone ?? fibeTokens.color.textPrimary, marginTop: '5px', letterSpacing: '-0.015em' }}>
                                {stat.value}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {actionError && (
                    <Box sx={{ padding: '0 18px 14px' }}>
                        <ErrorBanner title="Action failed" message={actionError} />
                    </Box>
                )}

                {showActionsRow && (
                    <Box sx={{ display: 'flex', gap: '10px', padding: '0 18px 16px' }}>
                        <Button variant="fibePrimary" size="small" onClick={handleApprove}>Mark Approved</Button>
                    </Box>
                )}

                <Box sx={{ padding: '0 18px 8px' }}>
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 11, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Line Items
                    </Box>
                </Box>
                <Box sx={{ paddingBottom: settlements.length === 0 ? '10px' : 0 }}>
                    <Table sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 140 }}>App ID</TableCell>
                                <TableCell size="fibe" variant="head" align="left">Institute</TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 170 }}>Milestone</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 130 }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoice.lineItems.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell
                                        size="fibe"
                                        variant="body"
                                        align="left"
                                        sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: fibeTokens.color.textSecondary }}
                                    >
                                        {item.applicationId}
                                    </TableCell>
                                    <TableCell size="fibe" variant="body" align="left">{item.instituteName}</TableCell>
                                    <TableCell size="fibe" variant="body" align="left">{item.milestoneType}</TableCell>
                                    <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {formatCurrency(item.amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {settlements.length > 0 && (
                    <>
                        <Box sx={{ padding: '16px 18px 8px' }}>
                            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 11, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Settlement History
                            </Box>
                        </Box>
                        <Table sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 150 }}>Date</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 150 }}>Amount</TableCell>
                                    <TableCell size="fibe" variant="head" align="left">UTR</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {settlements.map((settlement) => (
                                    <TableRow key={settlement.id}>
                                        <TableCell size="fibe" variant="body" align="left">
                                            {new Date(settlement.paymentDate).toLocaleDateString('en-IN')}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(settlement.paymentAmount)}
                                        </TableCell>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="left"
                                            sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: fibeTokens.color.textSecondary }}
                                        >
                                            {settlement.utrReference}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </Box>

            {(invoice.invoiceStatus === 'approved' || invoice.invoiceStatus === 'zoho_created' || invoice.invoiceStatus === 'partially_settled') && (
                <RecordSettlementForm invoice={invoice} onRecorded={refreshAfterAction} />
            )}
        </>
    );
}

function InvoicesTab() {
    const [invoices, setInvoices] = useState([]);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
    const [page, setPage] = useState(1);
    const [periodFilter, setPeriodFilter] = useState({ billingMonth: '', billingYear: '' });
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [periodOptions, setPeriodOptions] = useState([]);

    useEffect(() => {
        axiosInstance
            .get('/fibe-billing/invoices', { params: { page: 1, limit: ALL_PERIODS_FETCH_LIMIT } })
            .then((response) => {
                const distinctPeriods = Array.from(
                    new Map(
                        response.data.data.map((invoice) => [`${invoice.billingMonth}-${invoice.billingYear}`, invoice]),
                    ).values(),
                ).sort((a, b) => (a.billingYear - b.billingYear) || (a.billingMonth - b.billingMonth));
                setPeriodOptions(distinctPeriods);
            })
            .catch(() => setPeriodOptions([]));
    }, []);

    const updatePeriodFilter = (value) => {
        if (!value) {
            setPeriodFilter({ billingMonth: '', billingYear: '' });
        } else {
            const [billingMonth, billingYear] = value.split('-');
            setPeriodFilter({ billingMonth, billingYear });
        }
        setPage(1);
    };

    const loadInvoices = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        Promise.all([
            axiosInstance.get('/fibe-billing/invoices', {
                params: {
                    page,
                    limit: PAGE_SIZE,
                    billingMonth: periodFilter.billingMonth || undefined,
                    billingYear: periodFilter.billingYear || undefined,
                },
            }),
            axiosInstance.get('/fibe-billing/invoices/dashboard/summary'),
        ])
            .then(([listRes, summaryRes]) => {
                setInvoices(listRes.data.data);
                setMeta(listRes.data.meta);
                setSummary(summaryRes.data.data);
            })
            .catch(() => setErrorMessage('Could not load invoices.'))
            .finally(() => setIsLoading(false));
    }, [page, periodFilter.billingMonth, periodFilter.billingYear]);

    useEffect(() => {
        loadInvoices();
    }, [loadInvoices]);

    const selectedPeriodValue = periodFilter.billingMonth && periodFilter.billingYear
        ? `${periodFilter.billingMonth}-${periodFilter.billingYear}`
        : '';

    return (
        <div>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                <Box
                    component="h1"
                    sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}
                >
                    Invoices
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <Select
                        size="fibeFlatSm"
                        displayEmpty
                        value={selectedPeriodValue}
                        onChange={(e) => updatePeriodFilter(e.target.value)}
                        MenuProps={FILTER_MENU_PROPS}
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="">All Periods</MenuItem>
                        {periodOptions.map((invoice) => (
                            <MenuItem
                                key={`${invoice.billingMonth}-${invoice.billingYear}`}
                                value={`${invoice.billingMonth}-${invoice.billingYear}`}
                            >
                                {monthLabel(invoice.billingMonth, invoice.billingYear)}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="fibeSecondary"
                        disabled={isLoading}
                        onClick={loadInvoices}
                        startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshGlyph />}
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button variant="fibePrimary" onClick={() => setShowGenerateModal(true)}>
                        + Generate New Invoice
                    </Button>
                </Box>
            </Box>

            {summary && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: `${fibeTokens.density.gridGap}px`, marginBottom: '14px' }}>
                    {[
                        { key: 'invoiced', label: 'Total Invoiced (All Time)', value: formatCurrency(summary.totalInvoiced), tone: fibeTokens.color.success },
                        { key: 'received', label: 'Total Received', value: formatCurrency(summary.totalSettled), tone: fibeTokens.color.info },
                        { key: 'outstanding', label: 'Outstanding', value: formatCurrency(summary.totalOutstanding), tone: fibeTokens.color.warning },
                    ].map((kpi) => (
                        <Box
                            key={kpi.key}
                            sx={{
                                background: fibeTokens.color.surface,
                                border: `1px solid ${fibeTokens.color.border}`,
                                borderLeft: `3px solid ${kpi.tone}`,
                                borderRadius: `${fibeTokens.density.cardRadius}px`,
                                padding: fibeTokens.density.cardPadding,
                                boxShadow: fibeTokens.density.cardShadow,
                            }}
                        >
                            <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.kpiLabel, marginBottom: '10px' }}>
                                {kpi.label}
                            </Box>
                            <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.kpiValue, fontSize: 28, color: kpi.tone }}>
                                {kpi.value}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    overflow: 'hidden',
                    marginBottom: '14px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                    <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                        Invoices
                    </Box>
                </Box>

                {isLoading ? (
                    <InvoicesTableSkeleton />
                ) : errorMessage ? (
                    <Box sx={{ padding: '16px' }}>
                        <ErrorBanner title="Could not load invoices" message={errorMessage} onRetry={loadInvoices} />
                    </Box>
                ) : invoices.length === 0 ? (
                    <InvoicesEmptyState onGenerate={() => setShowGenerateModal(true)} />
                ) : (
                    <Table sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 150 }}>Invoice #</TableCell>
                                <TableCell size="fibe" variant="head" align="left">Period</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 120 }}>Amount</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 80 }}>Items</TableCell>
                                <TableCell size="fibe" variant="head" align="left" sx={{ width: 140 }}>Status</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 120 }}>Settled</TableCell>
                                <TableCell size="fibe" variant="head" align="right" sx={{ width: 100 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((invoice) => {
                                const isSelected = invoice.id === selectedInvoiceId;
                                return (
                                    <TableRow key={invoice.id} sx={isSelected ? { background: fibeTokens.color.primaryTint } : undefined}>
                                        <TableCell
                                            size="fibe"
                                            variant="body"
                                            align="left"
                                            sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, fontWeight: 600, color: fibeTokens.color.textPrimary }}
                                        >
                                            {invoice.invoiceNumber}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="left">
                                            {monthLabel(invoice.billingMonth, invoice.billingYear)}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(invoice.totalAmount)}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right">{invoice.lineItemCount}</TableCell>
                                        <TableCell size="fibe" variant="body" align="left">
                                            <StatusChip status={invoice.invoiceStatus} />
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(invoice.totalSettled)}
                                        </TableCell>
                                        <TableCell size="fibe" variant="body" align="right">
                                            <Button variant="fibeSecondary" size="small" onClick={() => setSelectedInvoiceId(invoice.id)}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {!errorMessage && meta.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <Button variant="fibeSecondary" size="small" disabled={page <= 1 || isLoading} onClick={() => setPage((p) => p - 1)}>
                        Prev
                    </Button>
                    <Box sx={{ fontFamily: fibeTokens.font.family, fontSize: 13, color: fibeTokens.color.textSecondary }}>
                        Page {page} of {meta.totalPages}
                    </Box>
                    <Button variant="fibeSecondary" size="small" disabled={page >= meta.totalPages || isLoading} onClick={() => setPage((p) => p + 1)}>
                        Next
                    </Button>
                </Box>
            )}

            {selectedInvoiceId && (
                <InvoiceDetailPanel
                    invoiceId={selectedInvoiceId}
                    onClose={() => setSelectedInvoiceId(null)}
                    onChanged={loadInvoices}
                />
            )}

            {showGenerateModal && (
                <GenerateInvoiceModal onClose={() => setShowGenerateModal(false)} onGenerated={loadInvoices} />
            )}
        </div>
    );
}

export default InvoicesTab;
